class BaseError extends Error { }

export class BadRequestException extends BaseError {
    statusCode = 400;
}

export class NotFoundError extends BaseError {
    statusCode = 404;
    id?: number | string;
}

export class ForbiddenError extends BaseError {
    statusCode = 403;
    constructor(public message: string) {
        super();
    }
}

export class BadParameterException extends BaseError {
    statusCode = 422;
}

export class UnauthenticatedError extends Error {
    errorMeta?: IUnauthenticatedErrorMeta; // seems obsolete to pass the window location around. Can just use it if we still need it for the signinRedirect
    kind = 'autherror';

    constructor(errorMeta?: IUnauthenticatedErrorMeta) {
        super();
        this.errorMeta = errorMeta;
    }

    /**
     * UnauthenticatedError will be an instanceof "Error" in client so this is simply a better typecheck
     */
    static isInstanceOf(error: any): error is UnauthenticatedError {
        return error instanceof UnauthenticatedError || (error instanceof Error && (error as UnauthenticatedError).kind === 'autherror');
    }

    static isUnauthenticated(error: any = {}): boolean {
        error = getPrimaryError(error);
        return UnauthenticatedError.isInstanceOf(error) || error.statusCode === 401 || error.message?.includes('login_required') /* Happens when IDP somehows generates mismatch between user_info.sub and current_sub */;
    }
}

class IUnauthenticatedErrorMeta {
    locationUri: string;
}


/**
 * Sometimes errors are wrapped, return real error in those cases
 * @param error
 */
export function getPrimaryError(error: any): any {
    if (error) {
        // Promise rejection, use rejection instead
        if (error.rejection) {
            return error.rejection;
        }
        // Errors from graphql
        else if (error.networkError) {
            return error.networkError;
        }
        // Errors from express?
        else if (error.originalError) {
            return error.originalError;
        }
    }
    return error || {};
}


interface ErrorContext {
    enabled?: boolean;
    level?: string;
    contexts?: { [key: string]: any; };
}

const defaultErrorContext: ErrorContext = { enabled: true, level: 'error' };

function isErrorContext(value: any): value is ErrorContext {
    return !!(value && value.contexts);
}

export function isNativeError(error: any): error is Error {
    return error instanceof Error;
}

export function handleError(message: string, context?: ErrorContext | unknown): void {
    let errorContext: ErrorContext;

    if (!isErrorContext(context)) {
        errorContext = { ...defaultErrorContext, ...{ contexts: { context } } };
    }
    else {
        errorContext = { ...defaultErrorContext, ...context };
    }

    const tracker = (window as any).TrackerService;

    if (errorContext.enabled && tracker) {
        tracker.captureMessage(message, errorContext.level, errorContext.contexts);
    }

    if (errorContext.level === 'error') {
        console.error(message, errorContext.contexts);
    } else {
        console.warn(message, errorContext.contexts);
    }
}
